import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { PropTypes } from "prop-types";
import React from "react";
import autoBind from "react-autobind";
import TeamMembersActions from "~/actions/team_members_actions";
import { LeadBucketAndRoleSelect } from "~/components/forms/lead_fields";
import TeamMemberInviteButton from "~/components/pages/account/team_members/TeamMemberInviteButton";
import Avatar from "~/components/shared/Avatar";
import DrawerContext from "~/contexts/DrawerContext";
import TeamMembersContext from "~/contexts/TeamMembersContext";
import brokerbit from "~/lib/brokerbit";
import dayjs from "~/lib/dayjs";

class TeamMemberItem extends React.Component {
  constructor(props) {
    super(props);

    this.ownershipRecoveryPreviewRequest = null;
    this.ownershipRecoveryPreviewTimer = null;
    this.ownershipRecoveryPreviewToken = 0;
    this.isUnmounted = false;

    autoBind(this);
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    this.cancelOwnershipRecoveryPreview();
  }

  onEditMemberClick(e) {
    e.preventDefault();

    const { teamMember, notify, drawerExternalRolesOnly } = this.props;
    const { selectedTab, searchQuery, currentPage } = this.context;

    this._openDrawer("team-member", {
      teamMember,
      memberRole: selectedTab,
      searchQuery,
      currentPage,
      notify,
      externalRolesOnly: drawerExternalRolesOnly,
    });
  }

  onRemoveMemberClick(e) {
    e.preventDefault();

    const { teamMember } = this.props;
    const { selectedTab } = this.context;

    let title;
    let message;
    let size;

    if (selectedTab === "admins" || selectedTab === "staff") {
      size = "large";
      title = `Are you sure you want to remove ${teamMember.name}?`;
      message =
        '<div>Note: <br/> <ul><li>Removing this user will remove their system access and they will be moved to Archive in Retention.</li><li>Please review all campaigns to determine if this user is set as a "Sender" and change the sender to a different user to avoid messages being sent later as this user.</li></ul></div>';
    } else {
      size = "small";
      title = "";
      message = `Are you sure you want to remove ${teamMember.name}?`;
    }

    this.confirmWithOwnershipRecoveryPreview({
      title,
      message,
      size,
      callback: () => {
        const { selectedTab, searchQuery, currentPage } = this.context;

        TeamMembersActions.deleteTeamMember(
          teamMember,
          selectedTab,
          searchQuery,
          currentPage,
        );
      },
    });
  }

  onSelectChange(opt) {
    const { teamMember } = this.props;
    const { selectedTab, searchQuery, currentPage } = this.context;

    switch (opt.value) {
      case "recruiting":
        brokerbit.confirmBox({
          message: `Are you sure you want to send ${teamMember.name} back to Recruiting and remove their access to Brokerkit?`,
          callback: (ok) => {
            if (ok) {
              TeamMembersActions.moveTeamMember(
                teamMember,
                "recruiting",
                selectedTab,
                searchQuery,
                currentPage,
              );
            }
          },
        });
        break;
      case "agent":
        brokerbit.confirmBox({
          message: `Are you sure you want to convert ${teamMember.name} to an Agent?`,
          callback: (ok) => {
            if (ok) {
              TeamMembersActions.moveTeamMember(
                teamMember,
                "agent",
                selectedTab,
                searchQuery,
                currentPage,
              );
            }
          },
        });
        break;
      case "affiliate":
        brokerbit.confirmBox({
          message: `Are you sure you want to convert ${teamMember.name} to an Affiliate?`,
          callback: (ok) => {
            if (ok) {
              TeamMembersActions.moveTeamMember(
                teamMember,
                "affiliate",
                selectedTab,
                searchQuery,
                currentPage,
              );
            }
          },
        });
        break;
      case "archived":
        this.confirmWithOwnershipRecoveryPreview({
          message: `Are you sure you'd like to move ${teamMember.name} to Archive and remove their access to Brokerkit?`,
          callback: () => {
            const { selectedTab, searchQuery, currentPage } = this.context;

            TeamMembersActions.moveTeamMember(
              teamMember,
              "archived",
              selectedTab,
              searchQuery,
              currentPage,
            );
          },
        });
        break;
      default:
        break;
    }
  }

  confirmWithOwnershipRecoveryPreview({
    title,
    message,
    size = "small",
    callback,
  }) {
    const { teamMember } = this.props;
    this.cancelOwnershipRecoveryPreview();

    const previewToken = this.ownershipRecoveryPreviewToken;
    let confirmationOpened = false;

    const openConfirmation = (preview) => {
      if (
        this.isUnmounted ||
        previewToken !== this.ownershipRecoveryPreviewToken ||
        confirmationOpened
      ) {
        return;
      }

      confirmationOpened = true;
      clearTimeout(this.ownershipRecoveryPreviewTimer);
      this.ownershipRecoveryPreviewTimer = null;
      this.ownershipRecoveryPreviewRequest = null;

      brokerbit.confirmBox({
        title,
        message: preview
          ? this.messageWithOwnershipRecoveryPreview(message, preview)
          : message,
        size,
        callback: (ok) => {
          if (ok) {
            callback();
          }
        },
      });
    };

    this.ownershipRecoveryPreviewTimer = setTimeout(
      () => openConfirmation(),
      750,
    );

    this.ownershipRecoveryPreviewRequest =
      TeamMembersActions.loadOwnershipRecoveryPreview(teamMember);
    this.ownershipRecoveryPreviewRequest.end((error, response) => {
      openConfirmation(error ? null : response.body);
    });
  }

  cancelOwnershipRecoveryPreview() {
    this.ownershipRecoveryPreviewToken += 1;
    clearTimeout(this.ownershipRecoveryPreviewTimer);
    this.ownershipRecoveryPreviewTimer = null;

    if (this.ownershipRecoveryPreviewRequest?.abort) {
      this.ownershipRecoveryPreviewRequest.abort();
    }
    this.ownershipRecoveryPreviewRequest = null;
  }

  messageWithOwnershipRecoveryPreview(message, preview = {}) {
    const ownedContactsCount = preview.owned_contacts_count || 0;

    if (ownedContactsCount === 0) {
      return message;
    }

    const contactLabel = ownedContactsCount === 1 ? "contact" : "contacts";
    const ownershipNotice = `<div class="mt-2"><strong>${ownedContactsCount} owned ${contactLabel}</strong> will be saved in a recovery snapshot before this access change.</div>`;

    return `<div>${message}</div>${ownershipNotice}`;
  }

  renderRoles() {
    const { teamMember } = this.props;
    const { name } = teamMember.role;

    let label = name.replace("_", " ").toUpperCase();

    let className;
    switch (name) {
      case "admin":
        className = "badge badge-outline-green";
        break;
      case "staff":
        className = "badge badge-outline-yellow";
        label = "STAFF";
        break;
      case "agent":
        className = "badge badge-outline-blue";
        break;
      default:
        className = "badge badge-outline-grey";
        break;
    }

    return (
      <span
        key={`role-${teamMember.role.id}`}
        className={classNames(className, "mr-1")}
      >
        {label}
      </span>
    );
  }

  render() {
    const { mode } = this.context;
    const { teamMember } = this.props;

    return (
      <DrawerContext.Consumer>
        {({ openDrawer }) => {
          this._openDrawer = openDrawer;
          return this.renderContent(mode, teamMember);
        }}
      </DrawerContext.Consumer>
    );
  }

  renderContent(mode, teamMember) {
    if (mode === "onboarding") {
      return (
        <tr>
          <td
            width="15%"
            className="d-none d-sm-table-cell"
            onClick={this.onEditMemberClick}
          >
            {this.renderRoles()}
          </td>
          <td width="50%" onClick={this.onEditMemberClick}>
            <Avatar
              name={teamMember?.name}
              avatar={teamMember?.avatar_url}
              sizeType="small"
            />{" "}
            {teamMember.name}
          </td>
          <td
            width="20%"
            className="d-none d-sm-table-cell"
            onClick={this.onEditMemberClick}
          >
            {teamMember.email}
          </td>
        </tr>
      );
    }
    const showRetentionSelect = ["agent", "affiliate"].includes(
      teamMember.role.name,
    );

    return (
      <tr>
        <td
          width="15%"
          className="d-none d-sm-table-cell"
          onClick={this.onEditMemberClick}
        >
          {this.renderRoles()}
        </td>
        <td width="50%" onClick={this.onEditMemberClick}>
          <Avatar
            name={teamMember?.name}
            avatar={teamMember?.avatar_url}
            sizeType="small"
          />{" "}
          {teamMember.name}
        </td>
        <td
          width="20%"
          className="d-none d-sm-table-cell"
          onClick={teamMember.role.joined_at && this.onEditMemberClick}
        >
          {teamMember.role.joined_at && (
            <span>
              Joined {dayjs(teamMember.role.joined_at).format("MMM Do YYYY")}
            </span>
          )}
          {!teamMember.role.joined_at &&
            (teamMember.role.invited_at ? (
              <TeamMemberInviteButton
                text="Resend Invite"
                memberId={teamMember.id}
              />
            ) : (
              <TeamMemberInviteButton
                text="Send Invite"
                memberId={teamMember.id}
              />
            ))}
        </td>
        <td width="15%" className="text-right">
          {showRetentionSelect ? (
            <LeadBucketAndRoleSelect
              role={teamMember.role}
              className="text-left"
              onChange={this.onSelectChange}
            />
          ) : (
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={this.onRemoveMemberClick}
            >
              <FontAwesomeIcon icon={["far", "fa-times"]} />
            </button>
          )}
        </td>
      </tr>
    );
  }
}

TeamMemberItem.defaultProps = {
  teamMember: null,
  mode: "normal",
  notify: false,
  drawerExternalRolesOnly: false,
};

TeamMemberItem.propTypes = {
  teamMember: PropTypes.shape({}),
  notify: PropTypes.bool,
  drawerExternalRolesOnly: PropTypes.bool,
  mode: PropTypes.string,
};

TeamMemberItem.contextType = TeamMembersContext;

export default TeamMemberItem;
